import { useState } from 'react';
import { TextArea, Input, TextButton } from 't-lib-ui/widgets';
import { checkmarkIcon, closeIcon } from '../icons';

export interface AITextAreaProps {
  value: string;
  onInput: (value: string) => void;
  placeholder?: string;
  rows?: number;
  label?: string;
  required?: boolean;
  onGenerate?: (instructions?: string) => Promise<string>;
  onImprove?: (text: string, instructions?: string) => Promise<string>;
  extraButtons?: React.ReactNode;
}

const AITextArea: React.FC<AITextAreaProps> = ({
  value,
  onInput,
  placeholder,
  rows = 3,
  label,
  required = false,
  onGenerate,
  onImprove,
  extraButtons
}) => {
  const [aiInstructions, setAiInstructions] = useState('');
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiResult, setAiResult] = useState<string | null>(null);

  const handleGenerate = async () => {
    if (!onGenerate || aiGenerating) return;

    try {
      setAiGenerating(true);
      const result = await onGenerate(aiInstructions || undefined);
      setAiResult(result);
    } catch (e) {
      console.error('Failed to generate text:', e);
      alert('Failed to generate text');
    } finally {
      setAiGenerating(false);
    }
  };

  const handleImprove = async () => {
    if (!onImprove || !value.trim() || aiGenerating) return;

    try {
      setAiGenerating(true);
      const result = await onImprove(value, aiInstructions || undefined);
      setAiResult(result);
    } catch (e) {
      console.error('Failed to improve text:', e);
      alert('Failed to improve text');
    } finally {
      setAiGenerating(false);
    }
  };

  const handleKeepAiResult = () => {
    if (aiResult) {
      onInput(aiResult);
      setAiResult(null);
      setAiInstructions('');
    }
  };

  const handleDiscardAiResult = () => {
    setAiResult(null);
  };

  return (
    <div style={{ marginBottom: '16px' }}>
      {label && (
        <label
          style={{
            display: 'block',
            fontWeight: 600,
            marginBottom: '8px',
            fontSize: '0.9rem',
            color: required && !value.trim() ? 'var(--error-color)' : 'var(--gray-350)'
          }}
        >
          {label}{required && ' (required)'}
        </label>
      )}
      <TextArea
        value={value}
        onInput={onInput}
        placeholder={placeholder}
        rows={rows}
        layout="singleCol"
        borderless={false}
      />

      {(onGenerate || onImprove) && !aiResult && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', marginTop: '4px' }}>
            <label style={{ margin: 0, minWidth: '30px', fontWeight: 600, fontSize: '0.9rem' }}>
              AI:
            </label>
            <Input
              value={aiInstructions}
              onInput={setAiInstructions}
              placeholder="Optional instructions for AI"
              layout="singleCol"
            />
          </div>
          <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
            {onGenerate && (
              <TextButton
                label="Generate"
                onClick={handleGenerate}
                focussable="flow"
                type="minor"
                busy={aiGenerating}
              />
            )}
            {onImprove && (
              <TextButton
                label="Improve"
                onClick={handleImprove}
                focussable="sideway"
                type="minor"
                busy={aiGenerating}
                enabled={value.trim() ? 'enabled' : 'disabled'}
              />
            )}
            {extraButtons}
          </div>
        </>
      )}

      {aiResult && (
        <div style={{
          marginTop: '12px',
          padding: '12px',
          background: 'var(--gray-975)',
          borderRadius: '4px',
          border: '1px solid var(--gray-850)'
        }}>
          <div style={{
            marginBottom: '12px',
            whiteSpace: 'pre-wrap',
            fontSize: '0.95rem',
            lineHeight: 1.5
          }}>
            {aiResult}
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <TextButton
              label="Keep"
              icon={checkmarkIcon}
              onClick={handleKeepAiResult}
              focussable="flow"
              type="major"
              size="small"
            />
            <TextButton
              label="Discard"
              icon={closeIcon}
              onClick={handleDiscardAiResult}
              focussable="flow"
              size="small"
              type="minor"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default AITextArea;
